import { Text } from "@/components/ui/baseUi/text"
import type { DailyScheduleSlotModel } from "@/modules/boxDashboard/modules/DailySchedule/models/dailyScheduleSlotModel"
import { word } from "@modules/core/utils/i18n"
import { cn } from "@modules/lib/utils"
import { Clock, UsersRound } from "lucide-react"
import { useState } from "react"

export const SLOT_HEIGHT = 60
export const SLOT_GAP = 8

type DailyScheduleCardProps = {
  slot: DailyScheduleSlotModel
  onClick: () => void
}

export function DailyScheduleCard({ slot, onClick }: DailyScheduleCardProps) {
  const [isHovered, setIsHovered] = useState(false)

  return (
    <button className="w-full self-start rounded-r-lg bg-surface-background" onClick={onClick}>
      <div
        className={cn(
          "relative cursor-pointer overflow-hidden rounded-r-lg border border-l-4 transition-all",
          slot.isPast && "opacity-50",
        )}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        style={{
          height: SLOT_HEIGHT,
          backgroundColor: `${slot.color}30`,
          borderRightColor: isHovered ? slot.color : `${slot.color}30`,
          borderTopColor: isHovered ? slot.color : `${slot.color}30`,
          borderBottomColor: isHovered ? slot.color : `${slot.color}30`,
          borderLeftColor: slot.color,
        }}
      >
        {/* Diagonal fill indicator - darker when empty, lighter when full */}
        <div
          className="absolute inset-0"
          style={{
            background: `linear-gradient(110deg, transparent 0%, transparent ${slot.fillPercentage}%, rgba(0,0,0, 0.5) ${slot.fillPercentage}%, rgba(0,0,0, 0.5) 100%)`,
          }}
        />

        {/* Content - Two columns */}
        <div className="relative z-10 flex h-full items-stretch justify-between gap-2 px-2 py-1">
          {/* Left column - top aligned */}
          <div className="flex min-w-0 flex-col items-start gap-0.5 overflow-hidden text-left">
            <Text fontStyle="LS" style={{ color: slot.color }}>
              {slot.timeRange}
            </Text>
            <Text fontStyle="LS" className="w-full truncate text-left text-textDefault-heading">
              {slot.programName}
            </Text>
            {slot.coachName && (
              <Text fontStyle="LS" className="w-full truncate text-left text-textDefault-body">
                {slot.coachName}
              </Text>
            )}
          </div>

          {/* Right column - vertically centered */}
          <div className="flex flex-col items-end justify-center gap-0.5">
            {slot.numberOfTrials !== null && (
              <Text fontStyle="LS" style={{ color: slot.color }}>
                {slot.numberOfTrials} {word("boxDashboard.dailySchedule.trials")}
              </Text>
            )}
            {slot.numberOfDropIns !== null && (
              <Text fontStyle="LS" style={{ color: slot.color }}>
                {slot.numberOfDropIns} {word("boxDashboard.dailySchedule.dropIn")}
              </Text>
            )}
            <div className="flex flex-row items-center">
              <Text fontStyle="LS" style={{ color: slot.color }}>
                {slot.numberOfActualParticipants}
              </Text>
              <UsersRound size={12} color={slot.color} className="mb-px" strokeWidth={2.5} />
              {slot.hasWaitlist && (
                <>
                  <Text fontStyle="LS" style={{ color: slot.color }} className="ml-0.5">
                    +{slot.numberOfPendingParticipants}
                  </Text>
                  <Clock size={12} color={slot.color} className="mb-px" strokeWidth={2.5} />
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </button>
  )
}
